import type { SimpleObservable, IOpenIDUpdate } from 'matrix-widget-api';
import {
  type Capability,
  type ISendDelayedEventDetails,
  type ISendEventDetails,
  type IReadEventRelationsResult,
  type IRoomEvent,
  WidgetDriver,
  type IWidgetApiErrorResponseDataDetails,
  type ISearchUserDirectoryResult,
  type IGetMediaConfigResult,
  UpdateDelayedEventAction,
  OpenIDRequestState,
} from 'matrix-widget-api';
import type { MatrixClient } from '$types/matrix-sdk';
import {
  EventType,
  type IContent,
  KnownMembership,
  MatrixError,
  type MatrixEvent,
  type Room,
  Direction,
  type SendDelayedEventResponse,
  type StateEvents,
  type TimelineEvents,
} from '$types/matrix-sdk';
import { getCallCapabilities } from './utils';
import { downloadMedia, mxcUrlToHttp, uploadContentToServer } from '../../utils/matrix';
import { createDebugLogger } from '../../utils/debugLogger';

const debugLog = createDebugLogger('CallWidgetDriver');

export const hydrateWidgetRoster = async (room: Room): Promise<void> => {
  try {
    await room.loadMembersIfNeeded();
  } catch (error) {
    // A partial roster still beats failing the read and stalling state sync.
    debugLog.warn('call', 'Failed to load room members for the call widget', {
      roomId: room.roomId,
      error: error instanceof Error ? error.message : String(error),
    });
    return;
  }

  const joinedInState = room.getMembersWithMembership(KnownMembership.Join).length;
  const joinedCount = room.getJoinedMemberCount();
  if (joinedInState < joinedCount) {
    debugLog.warn('call', 'Call widget roster is short of the joined member count', {
      roomId: room.roomId,
      joinedInState,
      joinedCount,
    });
  }
};

export class CallWidgetDriver extends WidgetDriver {
  private allowedCapabilities: Set<Capability>;

  private readonly mx: MatrixClient;

  public constructor(
    mx: MatrixClient,
    private inRoomId: string
  ) {
    super();
    this.mx = mx;

    const deviceId = mx.getDeviceId();
    if (!deviceId) {
      debugLog.error('call', 'Failed to initialize CallWidgetDriver - no device ID');
      throw new Error('Failed to initialize CallWidgetDriver! Device ID not found.');
    }

    debugLog.info('call', 'Initializing CallWidgetDriver', { roomId: inRoomId, deviceId });
    this.allowedCapabilities = getCallCapabilities(inRoomId, mx.getSafeUserId(), deviceId);
  }

  public async validateCapabilities(requested: Set<Capability>): Promise<Set<Capability>> {
    const requestedArray = Array.from(requested);
    const allow = requestedArray.filter((cap) => this.allowedCapabilities.has(cap));
    const denied = requestedArray.filter((cap) => !this.allowedCapabilities.has(cap));

    if (denied.length > 0) {
      debugLog.warn('call', 'Call widget requested unsupported capabilities', {
        roomId: this.inRoomId,
        deniedCapabilities: denied,
      });
    }

    return new Set(allow);
  }

  public async sendEvent(
    eventType: string,
    content: IContent,
    stateKey: string | null = null,
    targetRoomId: string | null = null
  ): Promise<ISendEventDetails> {
    const client = this.mx;
    const roomId = targetRoomId || this.inRoomId;

    if (!client || !roomId) {
      debugLog.error('call', 'Cannot send event - no client or room', { eventType, roomId });
      throw new Error('Not in a room or not attached to a client');
    }

    debugLog.info('call', 'Sending call event', {
      eventType,
      roomId,
      hasStateKey: stateKey !== null,
    });

    let r: { event_id: string } | null;
    if (typeof stateKey === 'string') {
      r = await client.sendStateEvent(
        roomId,
        eventType as keyof StateEvents,
        content as StateEvents[keyof StateEvents],
        stateKey
      );
    } else if (eventType === (EventType.RoomRedaction as string)) {
      // special case: extract the `redacts` property and call redact
      r = await client.redactEvent(roomId, content.redacts);
    } else {
      r = await client.sendEvent(
        roomId,
        eventType as keyof TimelineEvents,
        content as TimelineEvents[keyof TimelineEvents]
      );
    }

    debugLog.info('call', 'Call event sent successfully', { eventId: r.event_id, eventType });
    return { roomId, eventId: r.event_id };
  }

  public async sendDelayedEvent(
    delay: number,
    eventType: string,
    content: IContent,
    stateKey: string | null = null,
    targetRoomId: string | null = null
  ): Promise<ISendDelayedEventDetails> {
    const client = this.mx;
    const roomId = targetRoomId || this.inRoomId;

    if (!client || !roomId) throw new Error('Not in a room or not attached to a client');

    const delayOpts = { delay };

    let r: SendDelayedEventResponse | null;
    if (stateKey !== null) {
      // state event
      r = await client._unstable_sendDelayedStateEvent(
        roomId,
        delayOpts,
        eventType as keyof StateEvents,
        content as StateEvents[keyof StateEvents],
        stateKey
      );
    } else {
      // message event
      r = await client._unstable_sendDelayedEvent(
        roomId,
        delayOpts,
        null,
        eventType as keyof TimelineEvents,
        content as TimelineEvents[keyof TimelineEvents]
      );
    }

    return {
      roomId,
      delayId: r.delay_id,
    };
  }

  public async sendStickyEvent(
    stickyDurationMs: number,
    eventType: string,
    content: IContent,
    targetRoomId: string | null = null
  ): Promise<ISendEventDetails> {
    const client = this.mx;
    const roomId = targetRoomId || this.inRoomId;

    if (!client || !roomId) throw new Error('Not in a room or not attached to a client');

    const r = await client._unstable_sendStickyEvent(
      roomId,
      stickyDurationMs,
      null,
      eventType as keyof TimelineEvents,
      content as TimelineEvents[keyof TimelineEvents]
    );

    return { roomId, eventId: r.event_id };
  }

  public async sendDelayedStickyEvent(
    delay: number,
    stickyDurationMs: number,
    eventType: string,
    content: IContent,
    targetRoomId: string | null = null
  ): Promise<ISendDelayedEventDetails> {
    const client = this.mx;
    const roomId = targetRoomId || this.inRoomId;

    if (!client || !roomId) throw new Error('Not in a room or not attached to a client');

    const r = await client._unstable_sendStickyDelayedEvent(
      roomId,
      stickyDurationMs,
      { delay },
      null,
      eventType as keyof TimelineEvents,
      content as TimelineEvents[keyof TimelineEvents]
    );

    return { roomId, delayId: r.delay_id };
  }

  public async readStickyEvents(roomId: string): Promise<IRoomEvent[]> {
    const room = this.mx.getRoom(roomId);
    if (room === null) return [];

    return [...room._unstable_getStickyEvents()].map((e) => e.getEffectiveEvent() as IRoomEvent);
  }

  public async updateDelayedEvent(
    delayId: string,
    action: UpdateDelayedEventAction
  ): Promise<void> {
    const client = this.mx;

    if (!client) throw new Error('Not in a room or not attached to a client');

    await client._unstable_updateDelayedEvent(delayId, action);
  }

  public async cancelScheduledDelayedEvent(delayId: string): Promise<void> {
    await this.updateDelayedEvent(delayId, UpdateDelayedEventAction.Cancel);
  }

  public async restartScheduledDelayedEvent(delayId: string): Promise<void> {
    await this.updateDelayedEvent(delayId, UpdateDelayedEventAction.Restart);
  }

  public async sendScheduledDelayedEvent(delayId: string): Promise<void> {
    await this.updateDelayedEvent(delayId, UpdateDelayedEventAction.Send);
  }

  public async sendToDevice(
    eventType: string,
    encrypted: boolean,
    contentMap: Record<string, Record<string, object>>
  ): Promise<void> {
    const client = this.mx;

    if (encrypted) {
      const crypto = client.getCrypto();
      if (!crypto) throw new Error('E2EE not enabled');

      // attempt to re-batch these up into a single request
      const invertedContentMap: Record<string, { userId: string; deviceId: string }[]> = {};

      for (const userId of Object.keys(contentMap)) {
        const userContentMap = contentMap[userId];
        if (!userContentMap) continue;

        for (const deviceId of Object.keys(userContentMap)) {
          const content = userContentMap[deviceId];
          const stringifiedContent = JSON.stringify(content);
          invertedContentMap[stringifiedContent] = invertedContentMap[stringifiedContent] || [];
          invertedContentMap[stringifiedContent].push({ userId, deviceId });
        }
      }

      await Promise.all(
        Object.entries(invertedContentMap).map(async ([stringifiedContent, recipients]) => {
          const batch = await crypto.encryptToDeviceMessages(
            eventType,
            recipients,
            JSON.parse(stringifiedContent)
          );

          await client.queueToDevice(batch);
        })
      );
    } else {
      await client.queueToDevice({
        eventType,
        batch: Object.entries(contentMap).flatMap(([userId, userContentMap]) =>
          Object.entries(userContentMap).map(([deviceId, content]) => ({
            userId,
            deviceId,
            payload: content,
          }))
        ),
      });
    }
  }

  public async readRoomTimeline(
    roomId: string,
    eventType: string,
    msgtype: string | undefined,
    stateKey: string | undefined,
    limit: number,
    since: string | undefined
  ): Promise<IRoomEvent[]> {
    if (stateKey !== undefined) {
      return this.readRoomState(roomId, eventType, stateKey);
    }

    const stateEvents = await this.readRoomState(roomId, eventType, undefined);
    if (stateEvents.length > 0) return stateEvents;

    const safeLimit =
      limit > 0 ? Math.min(limit, Number.MAX_SAFE_INTEGER) : Number.MAX_SAFE_INTEGER; // relatively arbitrary

    const room = this.mx.getRoom(roomId);
    if (room === null) return [];
    const results: MatrixEvent[] = [];
    const events = room.getLiveTimeline().getEvents();

    for (let i = events.length - 1; i >= 0; i -= 1) {
      const ev = events[i];
      if (!ev) continue;
      if (results.length >= safeLimit) break;
      if (since !== undefined && ev.getId() === since) break;

      if (
        ev.getType() === eventType &&
        !ev.isState() &&
        (eventType !== (EventType.RoomMessage as string) ||
          !msgtype ||
          msgtype === ev.getContent().msgtype) &&
        (ev.getStateKey() === undefined || stateKey === undefined || ev.getStateKey() === stateKey)
      ) {
        results.push(ev);
      }
    }

    return results.map((e) => e.getEffectiveEvent() as IRoomEvent);
  }

  public async askOpenID(observer: SimpleObservable<IOpenIDUpdate>): Promise<void> {
    return observer.update({
      state: OpenIDRequestState.Allowed,
      token: await this.mx.getOpenIdToken(),
    });
  }

  public async readRoomState(
    roomId: string,
    eventType: string,
    stateKey: string | undefined
  ): Promise<IRoomEvent[]> {
    const room = this.mx.getRoom(roomId);
    if (room === null) return [];

    if (eventType === (EventType.RoomMember as string)) await hydrateWidgetRoster(room);

    const state = room.getLiveTimeline().getState(Direction.Forward);
    if (state === undefined) return [];

    if (stateKey === undefined)
      return state.getStateEvents(eventType).map((e) => e.getEffectiveEvent() as IRoomEvent);
    const event = state.getStateEvents(eventType, stateKey);
    return event === null ? [] : [event.getEffectiveEvent() as IRoomEvent];
  }

  public async readEventRelations(
    eventId: string,
    roomId?: string,
    relationType?: string,
    eventType?: string,
    from?: string,
    to?: string,
    limit?: number,
    direction?: 'f' | 'b'
  ): Promise<IReadEventRelationsResult> {
    const client = this.mx;
    const dir = direction as Direction;
    const targetRoomId = roomId ?? this.inRoomId ?? undefined;

    if (typeof targetRoomId !== 'string') {
      throw new Error('Error while reading the current room');
    }

    const { events, nextBatch, prevBatch } = await client.relations(
      targetRoomId,
      eventId,
      relationType ?? null,
      eventType ?? null,
      { from, to, limit, dir }
    );

    return {
      chunk: events.map((e) => e.getEffectiveEvent() as IRoomEvent),
      nextBatch: nextBatch ?? undefined,
      prevBatch: prevBatch ?? undefined,
    };
  }

  public async searchUserDirectory(
    searchTerm: string,
    limit?: number
  ): Promise<ISearchUserDirectoryResult> {
    const client = this.mx;

    const { limited, results } = await client.searchUserDirectory({ term: searchTerm, limit });

    return {
      limited,
      results: results.map((r) => ({
        userId: r.user_id,
        displayName: r.display_name,
        avatarUrl: r.avatar_url,
      })),
    };
  }

  public async getMediaConfig(): Promise<IGetMediaConfigResult> {
    const client = this.mx;

    return client.getMediaConfig();
  }

  public async uploadFile(file: XMLHttpRequestBodyInit): Promise<{ contentUri: string }> {
    const client = this.mx;

    const uploadResult = await uploadContentToServer(client, file);

    return { contentUri: uploadResult.content_uri };
  }

  public async downloadFile(contentUri: string): Promise<{ file: XMLHttpRequestBodyInit }> {
    const httpUrl = mxcUrlToHttp(this.mx, contentUri, true);
    if (!httpUrl) {
      throw new Error('Call widget failed to download file! No http url!');
    }
    const blob = await downloadMedia(httpUrl, {
      getAccessToken: () => this.mx.getAccessToken(),
      sessionScope: this.mx.getUserId() ?? undefined,
    });
    return { file: blob };
  }

  public getKnownRooms(): string[] {
    return this.mx.getVisibleRooms().map((r) => r.roomId);
  }

  public processError(error: unknown): IWidgetApiErrorResponseDataDetails | undefined {
    return error instanceof MatrixError
      ? { matrix_api_error: error.asWidgetApiErrorData() }
      : undefined;
  }
}
